import { Excalidraw, MainMenu } from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCcw, Save, X } from 'react-feather';
import useLayersStore from '../stores/layersStore';
import './ExcalidrawCanvas.css';

const ExcalidrawCanvas = () => {
  const excalidrawAPIRef = useRef(null);
  const setIsExDrawingMode = useLayersStore((state) => state.setIsExDrawingMode);
  const excalidrawBackground = useLayersStore((state) => state.excalidrawBackground);
  const [previewImage, setPreviewImage] = useState(null);
  const [fileName, setFileName] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Debug: Track render count (log every 100 renders)
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  if (renderCountRef.current == 1 || renderCountRef.current % 100 === 0) {
    console.log(`ExcalidrawCanvas render #${renderCountRef.current}`);
  }

  // Debug: Track what's causing re-renders
  const prevValuesRef = useRef({ excalidrawBackground: null });
  
  useEffect(() => {
    const prev = prevValuesRef.current;
    const current = { excalidrawBackground };
    
    const changes = [];
    if (prev.excalidrawBackground !== current.excalidrawBackground) {
      changes.push(`excalidrawBackground: ${prev.excalidrawBackground ? 'has data' : 'null'} -> ${current.excalidrawBackground ? 'has data' : 'null'}`);
    }
    
    if (changes.length > 0) {
      console.log('ExcalidrawCanvas values changed:', changes);
    }
    
    prevValuesRef.current = current;
  }, [excalidrawBackground]);

  const initialData = useMemo(() => ({
    elements: [],
    appState: {
      viewBackgroundColor: 'transparent',
      gridSize: null,
      currentItemFontFamily: 6,
    },
  }), []);

  // Hide the 3D model and pause Verge3D when Excalidraw opens
  useEffect(() => {
    const v3dContainer = document.getElementById('v3d-container');
    const v3dAnnoContainer = document.getElementById('v3d-anno-container');
    
    // Hide 3D model containers
    if (v3dContainer) {
      v3dContainer.style.display = 'none';
    }
    if (v3dAnnoContainer) {
      v3dAnnoContainer.style.display = 'none';
    }


    // Pause Verge3D rendering
    const v3dApp = useLayersStore.getState().v3dApp;
    if (v3dApp) {
      // Store original values to restore later
      const originalFrameRateDivider = v3dApp.frameRateDivider || 1; // Default to 1 if undefined
      const originalClockRunning = v3dApp.clock ? v3dApp.clock.running : false;
      
      // Try multiple methods to stop animation
      if (v3dApp.disableRenderTrigger) {
        v3dApp.disableRenderTrigger();
      }
      
      // Set frame rate divider to 0 to effectively stop rendering
      if (v3dApp.frameRateDivider !== undefined) {
        v3dApp.frameRateDivider = 0;
      }
      
      // Stop the clock/animation loop
      if (v3dApp.clock && v3dApp.clock.stop) {
        v3dApp.clock.stop();
      }
      
      // Stop all animation actions
      if (v3dApp.mixer && v3dApp.mixer.stopAllAction) {
        v3dApp.mixer.stopAllAction();
      }
      
      // Store original values for restoration
      window._originalVerge3DState = {
        frameRateDivider: originalFrameRateDivider,
        clockRunning: originalClockRunning
      };
    }

    return () => {
      // Show 3D model containers
      if (v3dContainer) {
        v3dContainer.style.display = '';
      }
      if (v3dAnnoContainer) {
        v3dAnnoContainer.style.display = '';
      }

      // Re-enable Verge3D rendering
      if (v3dApp && window._originalVerge3DState) {
        try {
          // Restore original frame rate divider
          if (v3dApp.frameRateDivider !== undefined) {
            const restoredValue = window._originalVerge3DState.frameRateDivider || 1;
            v3dApp.frameRateDivider = restoredValue;
          }
          
          // Re-enable rendering
          if (typeof v3dApp.enableRender === 'function') {
            v3dApp.enableRender();
          }
          if (typeof v3dApp.enableRenderTrigger === 'function') {
            v3dApp.enableRenderTrigger();
          }
          
          // Restart the clock/animation loop
          if (v3dApp.clock && typeof v3dApp.clock.start === 'function') {
            v3dApp.clock.start();
          }
          
          // Clean up stored state
          delete window._originalVerge3DState;
          
        } catch (error) {
          console.error('Error restoring Verge3D state in cleanup:', error);
          // Still try to clean up
          delete window._originalVerge3DState;
        }
      }
    };
  }, []);

  const handleExcalidrawAPI = useCallback((api) => {
    console.log('Excalidraw API set:', api);
    excalidrawAPIRef.current = api;
  }, []);
  /*
    // Add background immediately when API is ready
    if (excalidrawBackground) {
      console.log('Adding background from API callback');
      const addBackground = async () => {
        try {
          const fileId = 'bg-image';
          const byteString = atob(excalidrawBackground.split(',')[1]);
          const mimeString = excalidrawBackground.split(',')[0].split(':')[1].split(';')[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: mimeString });
          const file = new File([blob], 'background.png', { type: mimeString });
          
          api.addFiles([file]);
          console.log('File added');
          
          const imageElement = {
            type: 'image',
            version: 1,
            versionNonce: Math.random(),
            isDeleted: false,
            id: 'background-image',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 1,
            opacity: 100,
            angle: 0,
            x: 0,
            y: 0,
            strokeColor: '#000',
            backgroundColor: 'transparent',
            width: 800,
            height: 600,
            seed: 1,
            groupIds: [],
            frameId: undefined,
            roundness: null,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: true,
            fileId: fileId,
          };
          
          api.updateScene({
            elements: [imageElement],
            commitToFrame: true,
          });
          
          console.log('Background added from API callback');
        } catch (error) {
          console.error('Failed to add background:', error);
        }
      };
      
      addBackground();
    }
  }, [excalidrawBackground]); */
  const handleSave = useCallback(async () => {
    console.log('Save clicked');
    if (!excalidrawAPIRef.current) {
      console.error('Excalidraw API not available');
      return;
    }

    console.log('Setting saving state to true');
    setIsSaving(true);
    setSaveProgress(0);
    console.log('Saving state set, progress: 0');

    try {
      // Get scene elements from Excalidraw
      const elements = excalidrawAPIRef.current.getSceneElements();
      console.log('Saving elements:', elements.length);
      setSaveProgress(10);

      if (!excalidrawBackground) {
        console.error('No background image available');
        setIsSaving(false);
        return;
      }

      setSaveProgress(20);
      
      // Hide Excalidraw tools/UI before capturing
      const excalidrawContainer = document.querySelector('.excalidraw-container');
      if (!excalidrawContainer) {
        console.error('Excalidraw container not found');
        setIsSaving(false);
        return;
      }
      setSaveProgress(30);
      

      // Hide Excalidraw UI elements (but keep our custom buttons visible)
      const hiddenElements = [];
      
      // Don't hide our custom buttons - we want to show progress
      // const buttonContainer = excalidrawContainer.querySelector('.absolute.top-4.right-4');
      // if (buttonContainer) {
      //   buttonContainer.style.display = 'none';
      //   hiddenElements.push(buttonContainer);
      // }
      
      // Hide Excalidraw's main UI elements
      const excalidrawUI = excalidrawContainer.querySelector('.excalidraw');
      if (excalidrawUI) {
        // Hide the toolbar
        const toolbar = excalidrawUI.querySelector('[data-testid="toolbar"]') || 
                       excalidrawUI.querySelector('.ToolIcon') ||
                       excalidrawUI.querySelector('.App-toolbar-container');
        if (toolbar) {
          toolbar.style.display = 'none';
          hiddenElements.push(toolbar);
        }
        
        // Hide the mobile menu
        const mobileMenu = excalidrawUI.querySelector('.App-mobile-menu') ||
                          excalidrawUI.querySelector('[data-testid="mobile-menu"]');
        if (mobileMenu) {
          mobileMenu.style.display = 'none';
          hiddenElements.push(mobileMenu);
        }
        
        // Hide zen mode button
        const zenModeButton = excalidrawUI.querySelector('.zen-mode-transition') ||
                             excalidrawUI.querySelector('[data-testid="zen-mode-toggle"]');
        if (zenModeButton) {
          zenModeButton.style.display = 'none';
          hiddenElements.push(zenModeButton);
        }
        
        // Hide any other UI overlays
        const uiOverlays = excalidrawUI.querySelectorAll('.App-menu, .App-sidebar, .App-footer');
        uiOverlays.forEach(overlay => {
          overlay.style.display = 'none';
          hiddenElements.push(overlay);
        });
        
        // Hide text elements and overlays that might appear in upper left
        const textElements = excalidrawUI.querySelectorAll('.App-toolbar, .App-toolbar-content, .App-toolbar-mobile, .App-toolbar-mobile-content');
        textElements.forEach(element => {
          element.style.display = 'none';
          hiddenElements.push(element);
        });
        
        // Hide specific UI text elements that might be overlaying
        const uiTextElements = excalidrawUI.querySelectorAll(
          '.App-toolbar .ToolIcon__label, ' +
          '.App-toolbar .ToolIcon__keybinding, ' +
          '.App-toolbar .ToolIcon__icon, ' +
          '.App-menu .App-menu-item, ' +
          '.App-menu .App-menu-item-label, ' +
          '.App-sidebar .App-sidebar-title, ' +
          '.App-footer .App-footer-title, ' +
          '[data-testid="toolbar"] .ToolIcon, ' +
          '[data-testid="mobile-menu"] .App-menu-item'
        );
        uiTextElements.forEach(element => {
          element.style.display = 'none';
          hiddenElements.push(element);
        });
      }
      
      // Add CSS to hide "Drawing canvas" text on the canvas element
      const style = document.createElement('style');
      style.textContent = `
        .excalidraw__canvas {
          color: transparent !important;
          text-shadow: none !important;
          font-size: 0 !important;
        }
      `;
      document.head.appendChild(style);
      
      // Use html2canvas to capture everything (background + elements) without tools
      setSaveProgress(50);
      const { default: html2canvas } = await import('html2canvas');
      const finalCanvas = await html2canvas(excalidrawContainer, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        scale: 1,
        width: excalidrawContainer.offsetWidth,
        height: excalidrawContainer.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight
      });
      
      // Restore the hidden elements
      hiddenElements.forEach(element => {
        element.style.display = '';
      });
      
      // Remove the temporary CSS
      document.head.removeChild(style);
      setSaveProgress(90);
      
      const finalDataURL = finalCanvas.toDataURL('image/png');
      setSaveProgress(100);
      
      // Show preview modal
      showPreviewModal(finalDataURL);
      setIsSaving(false);
      
    } catch (error) {
      console.error('Save failed:', error);
      setIsSaving(false);
      setSaveProgress(0);
    }
  }, [excalidrawBackground]);

  const showPreviewModal = useCallback((imageDataURL) => {
    setPreviewImage(imageDataURL);
    setFileName(`excalidraw-export-${Date.now()}`);
    setShowPreview(true);
  }, []);

  const handleDownload = useCallback(() => {
    if (!previewImage) return;
    
    setIsDownloading(true);
    
    // Simulate a brief delay for better UX
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = previewImage;
      link.download = `${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Close preview
      setShowPreview(false);
      setPreviewImage(null);
      setFileName('');
      setIsDownloading(false);
    }, 500);
  }, [previewImage, fileName]);

  const handleCancelPreview = useCallback(() => {
    setShowPreview(false);
    setPreviewImage(null);
    setFileName('');
  }, []);

  const handleClear = useCallback(() => {
    console.log('Clear clicked');
    if (excalidrawAPIRef.current) {
      excalidrawAPIRef.current.updateScene({ elements: [] });
    }
  }, []);

  const handleClose = useCallback(() => {
    console.log('Close clicked - starting close process');
    
    try {
      // Manually re-enable Verge3D when closing
      const v3dApp = useLayersStore.getState().v3dApp;
      if (v3dApp) {
        console.log('Verge3D app methods available:', Object.keys(v3dApp));
        
        // Re-enable Verge3D rendering
        if (window._originalVerge3DState) {
          try {
            // Restore original frame rate divider
            if (v3dApp.frameRateDivider !== undefined) {
              const restoredValue = window._originalVerge3DState.frameRateDivider || 1;
              v3dApp.frameRateDivider = restoredValue;
            }
            
            // Re-enable rendering methods
            if (typeof v3dApp.enableRender === 'function') {
              v3dApp.enableRender();
            }
            if (typeof v3dApp.enableRenderTrigger === 'function') {
              v3dApp.enableRenderTrigger();
            }
            
            // Restart the clock/animation loop
            if (v3dApp.clock && typeof v3dApp.clock.start === 'function') {
              v3dApp.clock.start();
            }
            
            // Clean up stored state
            delete window._originalVerge3DState;
            
          } catch (error) {
            console.error('Error restoring Verge3D state:', error);
            // Still try to clean up
            delete window._originalVerge3DState;
          }
        }
      } else {
        console.log('No Verge3D app found in store');
      }
      
      console.log('Setting isExDrawingMode to false');
      setIsExDrawingMode(false);
      console.log('Close process completed');
      
    } catch (error) {
      console.error('Error during close process:', error);
      // Still try to close even if there's an error
      setIsExDrawingMode(false);
    }
  }, [setIsExDrawingMode]);

  return (
    <>
      {/* Control buttons - positioned outside the capture area */}
      <div className="excalidraw-custom-buttons fixed bottom-20 md:bottom-4 right-4 md:right-16 flex gap-2 min-h-9 z-[55]">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-md shadow-md hover:bg-white transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600"></div>
              {saveProgress}%
            </>
          ) : (
            <>
              <Save size={14} />
              <span className="hidden md:inline">Save</span>
            </>
          )}
        </button>
        <button
          onClick={handleClear}
          className="flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-md shadow-md hover:bg-white transition-colors text-sm font-medium"
        >
          <>
            <RefreshCcw size={14} />
            <span className="hidden md:inline">Clear</span>
          </>
        </button>
        <button
          onClick={handleClose}
          className="flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-md shadow-md hover:bg-white transition-colors text-sm font-medium"
        >
          <>
            <X size={14} />
            <span className="hidden md:inline">Close</span>
          </>
        </button>
      </div>

      {/* Excalidraw container - this is what gets captured */}
      <div className="fixed inset-0 z-50 bg-transparent excalidraw-container" style={{ 
          backgroundImage: excalidrawBackground ? `url(${excalidrawBackground})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
        <div className="w-full h-screen relative">
          <Excalidraw
            excalidrawAPI={handleExcalidrawAPI}
            initialData={initialData}
            showDeprecatedFonts={true}
            validateEmbeddable={(link) => {
              try {
                const url = new URL(link);
                const hostname = url.hostname.toLowerCase();
                if (hostname.includes('biocloud3d.com') || hostname.includes('biocloud3d.dev')) return true;
                const allowedDomains = ['youtube.com', 'youtu.be', 'vimeo.com', 'twitter.com', 'x.com', 'excalidraw.com'];
                return allowedDomains.some(domain => hostname.includes(domain));
              } catch {
                return false;
              }
            }}
          >
            <MainMenu>
              <MainMenu.DefaultItems.LoadScene />
              <MainMenu.DefaultItems.SaveToActiveFile />
              <MainMenu.DefaultItems.Export />
              <MainMenu.DefaultItems.SaveAsImage />
              <MainMenu.DefaultItems.SearchMenu />
              <MainMenu.DefaultItems.Help />
              <MainMenu.DefaultItems.ClearCanvas />
              <MainMenu.Separator />
              <MainMenu.DefaultItems.ToggleTheme />
              <MainMenu.DefaultItems.ChangeCanvasBackground />
            </MainMenu>
          </Excalidraw>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[90vh] overflow-auto">
            <h3 className="text-xl font-semibold mb-4">Export Preview</h3>
            
            {/* Image Preview */}
            <div className="mb-4 border rounded-lg overflow-hidden">
              <img 
                src={previewImage} 
                alt="Export preview" 
                className="max-w-full h-auto"
                style={{ maxHeight: '400px' }}
              />
            </div>
            
            {/* File Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">File Name:</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter file name"
              />
            </div>
            
                   {/* Action Buttons */}
                   <div className="flex gap-3 justify-end">
                     <button
                       onClick={handleCancelPreview}
                       disabled={isDownloading}
                       className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                     >
                       {isDownloading ? (
                         <>
                           <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                           Processing...
                         </>
                       ) : (
                         'Cancel'
                       )}
                     </button>
                     <button
                       onClick={handleDownload}
                       disabled={isDownloading}
                       className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                     >
                       {isDownloading ? (
                         <>
                           <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                           Downloading...
                         </>
                       ) : (
                         'Download'
                       )}
                     </button>
                   </div>
          </div>
        </div>
      )}
    </>
  );
};

export default memo(ExcalidrawCanvas);